'use client';

import React, { useState, useEffect, useRef } from 'react';
import s from './TableOfContents.module.scss';
import classNames from 'classnames';
import Icon from '../Icon';
import Bookmark from '../Bookmark/Bookmark';
import { CollapsibleMotion } from '../Animate/Animate';

const groupHeadings = (headings, showIntroduction) => {
    const sections = [];

    if (showIntroduction) {
        sections.push({
            text: 'Introduction',
            id: 'introduction',
            isIntro: true,
            children: [],
        });
    }

    if (headings.length === 0) return sections;

    const levels = headings.map((h) =>
        parseInt(h.level?.replace('h', '') || '2', 10)
    );
    const minLevel = Math.min(...levels);

    headings.forEach((heading) => {
        const headingLevel = parseInt(
            heading.level?.replace('h', '') || minLevel,
            10
        );
        if (headingLevel <= minLevel) {
            sections.push({ ...heading, children: [] });
        } else {
            if (sections.length > 0) {
                sections[sections.length - 1].children.push(heading);
            }
        }
    });

    return sections;
};

const TableOfContents = ({
    headings = [],
    onHeadingClick,
    onHeadingInView,
    onScrollSyncLockChange,
    activeHeading,
    showIntroduction = true,
    bookmarkCategories = [],
    bookmarkTitle = '',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [stickyOpen, setStickyOpen] = useState(false);
    const [isOutOfView, setIsOutOfView] = useState(false);
    const [scrollingUp, setScrollingUp] = useState(false);
    const classes = classNames(s.Root, { [s['Root--IsOpen']]: isOpen });
    const baseUrl =
        typeof window !== 'undefined' ? window.location.pathname : '';
    const navRef = useRef(null);
    const stickyWrapperRef = useRef(null);
    const scrollLockRef = useRef(false);
    const lastScrollY = useRef(0);
    const isInteractingWithMenuRef = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => setIsOutOfView(!entry.isIntersecting),
            { threshold: 0 }
        );
        if (navRef.current) observer.observe(navRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const handleScrollDirection = () => {
            if (isInteractingWithMenuRef.current) return;
            const currentY = window.scrollY;
            setScrollingUp(currentY < lastScrollY.current);
            lastScrollY.current = currentY;
        };
        window.addEventListener('scroll', handleScrollDirection, {
            passive: true,
        });
        return () =>
            window.removeEventListener('scroll', handleScrollDirection);
    }, []);

    const sections = groupHeadings(headings, showIntroduction);

    const isSectionActive = (section) =>
        activeHeading === section.text ||
        section.children.some((c) => c.text === activeHeading);

    const [expandedIds, setExpandedIds] = useState(() => {
        const active = sections.find(isSectionActive);
        return active ? [active.id] : [];
    });

    useEffect(() => {
        const activeSection = sections.find(isSectionActive);
        if (activeSection) {
            setExpandedIds([activeSection.id]);
        }
    }, [activeHeading]);

    const toggleSection = (id) => {
        setExpandedIds((prev) => (prev.includes(id) ? [] : [id]));
    };

    const setScrollLock = (value) => {
        if (scrollLockRef.current === value) return;
        scrollLockRef.current = value;
        onScrollSyncLockChange?.(value);
    };

    useEffect(() => {
        return () => {
            if (scrollLockRef.current) setScrollLock(false);
        };
    }, [onScrollSyncLockChange]);

    const handleClick = (e, id, text) => {
        e.preventDefault();
        setScrollLock(true);
        setIsOpen(false);
        setStickyOpen(false);
        onHeadingClick?.(text);
        if (id) {
            window.history.pushState(null, '', `#${id}`);
        }
    };

    useEffect(() => {
        const handleScroll = (event) => {
            if (scrollLockRef.current) return;

            let scrollContainer = null;
            if (event?.target && event.target instanceof Element) {
                scrollContainer = event.target;
            }

            if (!scrollContainer) {
                const containers = document.querySelectorAll(
                    '.Container, [class*="Container"]'
                );
                for (let container of containers) {
                    const style = window.getComputedStyle(container);
                    if (
                        style.overflowY === 'auto' ||
                        style.overflowY === 'scroll'
                    ) {
                        scrollContainer = container;
                        break;
                    }
                }
            }

            let closestHeading = null;
            let closestDistance = Infinity;
            const allHeadingElements = document.querySelectorAll(
                'h1, h2, h3, h4, h5, h6'
            );

            for (let i = 0; i < headings.length; i++) {
                const { id, text } = headings[i];
                let el = document.getElementById(id);
                if (!el) {
                    el = Array.from(allHeadingElements).find(
                        (h) => h.textContent?.trim() === text
                    );
                }
                if (el) {
                    const rect = el.getBoundingClientRect();
                    const containerRect =
                        scrollContainer && scrollContainer instanceof Element
                            ? scrollContainer.getBoundingClientRect()
                            : { top: 0 };
                    const relativeTop = rect.top - containerRect.top;
                    const distance = Math.abs(relativeTop);
                    if (relativeTop <= 50 && distance < closestDistance) {
                        closestDistance = distance;
                        closestHeading = text;
                    }
                }
            }

            onHeadingInView?.(closestHeading || 'Table of contents');
        };

        setTimeout(() => handleScroll(), 100);

        const containers = document.querySelectorAll(
            '.Container, [class*="Container"]'
        );
        const scrollableContainers = [];

        const handlePointerDown = (event) => {
            if (!scrollLockRef.current) return;
            if (
                navRef.current &&
                typeof Node !== 'undefined' &&
                event.target instanceof Node &&
                navRef.current.contains(event.target)
            )
                return;
            setScrollLock(false);
        };

        for (let container of containers) {
            const style = window.getComputedStyle(container);
            if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
                container.addEventListener('scroll', handleScroll, {
                    passive: true,
                });
                container.addEventListener('pointerdown', handlePointerDown);
                scrollableContainers.push(container);
            }
        }

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            scrollableContainers.forEach((container) => {
                container.removeEventListener('scroll', handleScroll);
                container.removeEventListener('pointerdown', handlePointerDown);
            });
        };
    }, [headings, onHeadingInView, onScrollSyncLockChange]);

    useEffect(() => {
        const handleWheel = () => {
            if (scrollLockRef.current) setScrollLock(false);
        };
        const handleTouchStart = (event) => {
            if (!scrollLockRef.current) return;
            if (
                navRef.current &&
                typeof Node !== 'undefined' &&
                event.target instanceof Node &&
                navRef.current.contains(event.target)
            )
                return;
            setScrollLock(false);
        };
        const scrollKeys = new Set([
            'ArrowDown',
            'ArrowUp',
            'PageDown',
            'PageUp',
            'Home',
            'End',
            ' ',
            'Spacebar',
            'Space',
        ]);
        const handleKeyDown = (event) => {
            if (!scrollLockRef.current) return;
            if (!scrollKeys.has(event.key)) return;
            setScrollLock(false);
        };

        window.addEventListener('wheel', handleWheel, { passive: true });
        window.addEventListener('touchstart', handleTouchStart, {
            passive: true,
        });
        document.addEventListener('pointerdown', handleTouchStart);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('wheel', handleWheel);
            window.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('pointerdown', handleTouchStart);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onScrollSyncLockChange]);

    useEffect(() => {
        if (!stickyOpen && !isOpen) return;

        const prevBodyOverflow = document.body.style.overflow;
        const prevHtmlOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        const preventOutsideMove = (e) => {
            if (stickyWrapperRef.current?.contains(e.target)) return;
            if (navRef.current?.contains(e.target)) return;
            if (e.cancelable) e.preventDefault();
        };
        document.addEventListener('touchmove', preventOutsideMove, {
            passive: false,
        });

        return () => {
            document.body.style.overflow = prevBodyOverflow;
            document.documentElement.style.overflow = prevHtmlOverflow;
            document.removeEventListener('touchmove', preventOutsideMove);
        };
    }, [stickyOpen, isOpen]);

    const isStickyVisible = isOutOfView && scrollingUp;

    useEffect(() => {
        if (!isStickyVisible) setStickyOpen(false);
    }, [isStickyVisible]);

    return (
        <nav className={classes} ref={navRef}>
            <div
                ref={stickyWrapperRef}
                className={classNames(s.StickyWrapper, {
                    [s['StickyWrapper--Visible']]: isStickyVisible,
                    [s['StickyWrapper--Open']]: stickyOpen,
                })}
                onPointerDown={() => {
                    isInteractingWithMenuRef.current = true;
                }}
                onPointerUp={() => {
                    isInteractingWithMenuRef.current = false;
                }}
                onPointerCancel={() => {
                    isInteractingWithMenuRef.current = false;
                }}
                onPointerLeave={() => {
                    isInteractingWithMenuRef.current = false;
                }}>
                <div
                    className={s.StickyTrigger}
                    onClick={() => setStickyOpen(!stickyOpen)}>
                    <div className={s.MobileDropdownLabel}>
                        <span>{activeHeading}</span>
                        <Icon
                            name={stickyOpen ? 'Close' : 'Toc'}
                            className={s.MobileDropdownIcon}
                        />
                    </div>
                </div>
                <ul className={s.StickyList}>
                    {sections.map((section) => {
                        const isExpanded = expandedIds.includes(section.id);
                        const isActive = isSectionActive(section);
                        const hasChildren = section.children.length > 0;
                        return (
                            <li
                                key={section.id}
                                className={classNames(s.Section, {
                                    [s['Section--IsActive']]: isActive,
                                    [s['Section--IsExpanded']]: isExpanded,
                                    [s['Section--HasChildren']]: hasChildren,
                                })}>
                                <div className={s.SectionHeader}>
                                    <div className={s.SectionWrapper}>
                                        <Bookmark
                                            url={`${baseUrl}#${section.id}`}
                                            isSmall
                                            categories={bookmarkCategories}
                                            alternativeTitle={section.text}
                                            modifiers={['TableOfContents']}
                                            isBlack={isActive}
                                        />
                                        <a
                                            href={`#${section.id}`}
                                            className={s.SectionLink}
                                            onClick={(e) =>
                                                handleClick(
                                                    e,
                                                    section.id,
                                                    section.text
                                                )
                                            }></a>
                                        <span className={s.SectionLinkText}>
                                            {section.text}
                                        </span>
                                    </div>
                                    {hasChildren && (
                                        <button
                                            aria-expanded={isExpanded}
                                            onClick={() =>
                                                toggleSection(section.id)
                                            }
                                            className={s.ToggleWrapper}>
                                            <div className={s.Toggle}>
                                                <span
                                                    className={s.ToggleIcon}
                                                />
                                            </div>
                                        </button>
                                    )}
                                </div>
                                {hasChildren && (
                                    <CollapsibleMotion isOpen={isExpanded}>
                                        <ul className={s.Children}>
                                            {section.children.map((child) => (
                                                <li
                                                    key={child.id}
                                                    className={classNames(
                                                        s.Child,
                                                        {
                                                            [s[
                                                                'Child--IsActive'
                                                            ]]:
                                                                activeHeading ===
                                                                child.text,
                                                        }
                                                    )}>
                                                    <a
                                                        href={`#${child.id}`}
                                                        className={s.ChildLink}
                                                        onClick={(e) =>
                                                            handleClick(
                                                                e,
                                                                child.id,
                                                                child.text
                                                            )
                                                        }>
                                                        {child.text}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </CollapsibleMotion>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
            {stickyOpen && isStickyVisible && (
                <div
                    className={s.StickyOverlay}
                    onPointerDown={() => setStickyOpen(false)}
                />
            )}
            <div className={s.Overlay} onPointerDown={() => setIsOpen(false)} />
            <div
                className={s.MobileDropdown}
                onClick={() => setIsOpen(!isOpen)}>
                <div className={s.MobileDropdownLabel}>
                    <span>{activeHeading}</span>
                    <Icon
                        name={isOpen ? 'Close' : 'Toc'}
                        className={s.MobileDropdownIcon}
                    />
                </div>
            </div>

            <ul className={s.List}>
                {sections.map((section) => {
                    const isExpanded = expandedIds.includes(section.id);
                    const isActive = isSectionActive(section);
                    const hasChildren = section.children.length > 0;

                    return (
                        <li
                            key={section.id}
                            className={classNames(s.Section, {
                                [s['Section--IsActive']]: isActive,
                                [s['Section--IsExpanded']]: isExpanded,
                                [s['Section--HasChildren']]: hasChildren,
                            })}>
                            <div className={s.SectionHeader}>
                                <div className={s.SectionWrapper}>
                                    <Bookmark
                                        url={`${baseUrl}#${section.id}`}
                                        isSmall
                                        categories={bookmarkCategories}
                                        alternativeTitle={section.text}
                                        modifiers={['TableOfContents']}
                                        isBlack={isActive}
                                    />
                                    <a
                                        href={`#${section.id}`}
                                        className={s.SectionLink}
                                        onClick={(e) =>
                                            handleClick(
                                                e,
                                                section.id,
                                                section.text
                                            )
                                        }></a>
                                    <span className={s.SectionLinkText}>
                                        {section.text}
                                    </span>
                                </div>
                                {hasChildren && (
                                    <button
                                        aria-expanded={isExpanded}
                                        onClick={() =>
                                            toggleSection(section.id)
                                        }
                                        className={s.ToggleWrapper}>
                                        <div className={s.Toggle}>
                                            <span className={s.ToggleIcon} />
                                        </div>
                                    </button>
                                )}
                            </div>

                            {hasChildren && (
                                <CollapsibleMotion isOpen={isExpanded}>
                                    <ul className={s.Children}>
                                        {section.children.map((child) => (
                                            <li
                                                key={child.id}
                                                className={classNames(s.Child, {
                                                    [s['Child--IsActive']]:
                                                        activeHeading ===
                                                        child.text,
                                                })}>
                                                <a
                                                    href={`#${child.id}`}
                                                    className={s.ChildLink}
                                                    onClick={(e) =>
                                                        handleClick(
                                                            e,
                                                            child.id,
                                                            child.text
                                                        )
                                                    }>
                                                    {child.text}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </CollapsibleMotion>
                            )}
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};

export default TableOfContents;
